import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useParams, Link, useLocation, useNavigate, useNavigationType } from "react-router-dom";
import { ChevronDown, SlidersHorizontal, X } from "lucide-react";
import { motion } from "framer-motion";
import { api, getEntity } from "../lib/api";
import { SEO, breadcrumbLd, collectionPageLd, itemListLd } from "../components/SEO";
import { ProductCard } from "../components/ProductCard";
import { ShopByTabs } from "../components/ShopByTabs";
import { saveLocationState, readLocationState } from "../components/ScrollManager";
import NotFound from "./NotFound";
import { LoadError } from "../components/LoadError";
import { renderMd } from "../lib/renderMd";

const PAGE_SIZE = 100;

function CategoryFAQs({ faqs }) {
  const [open, setOpen] = useState(0);
  if (!faqs || !faqs.length) return null;
  return (
    <section className="mt-16 border-t-2 border-black pt-8" data-testid="category-faq-section">
      <p className="text-xs font-bold uppercase tracking-[0.4em] text-[#1e40af]">FAQ</p>
      <h2 className="mt-3 font-display text-2xl font-black tracking-tighter text-black sm:text-3xl">Questions about this collection</h2>
      <ul className="mt-8 divide-y-2 divide-black border-y-2 border-black">
        {faqs.map((f, i) => {
          const isOpen = open === i;
          return (
            <li key={f.id} data-testid={`cat-faq-item-${i}`}>
              <button
                type="button"
                onClick={() => setOpen(isOpen ? -1 : i)}
                aria-expanded={isOpen}
                data-testid={`cat-faq-toggle-${i}`}
                className="flex w-full items-center justify-between gap-4 py-5 text-left transition-colors duration-200 hover:bg-zinc-50"
              >
                <h3 className="m-0 font-display text-base font-bold text-black">{f.question}</h3>
                <ChevronDown size={20} className={`shrink-0 text-black transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
              </button>
              <motion.div
                initial={false}
                animate={{ height: isOpen ? "auto" : 0, opacity: isOpen ? 1 : 0 }}
                transition={{ duration: 0.25 }}
                className="overflow-hidden"
              >
                <p className="pb-5 pr-8 text-sm leading-relaxed text-zinc-700">{f.answer}</p>
              </motion.div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function FiltersBar({ facets, priceRange, setPriceRange, selectedVariants, toggleVariant, inStockOnly, setInStockOnly, sort, setSort, activeCount, onReset }) {
  if (!facets) return null;
  return (
    <div className="border-y-2 border-black bg-white" data-testid="category-filters">
      <div className="flex flex-col gap-4 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2 sm:min-w-[120px]">
          <SlidersHorizontal size={16} />
          <h3 className="font-display text-sm font-black uppercase tracking-tight text-black">Filters</h3>
        </div>

        {/* Price */}
        <div className="flex flex-1 items-center gap-3">
          <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-500">Price</span>
          <div className="flex items-center gap-2">
            <input
              type="number"
              value={priceRange[0]}
              onChange={(e) => setPriceRange([Math.max(facets.min_price, +e.target.value || 0), priceRange[1]])}
              data-testid="filter-price-min-input"
              className="w-20 border-2 border-black bg-white px-2 py-2 text-xs text-black focus:outline-none"
              placeholder="Min"
            />
            <span className="text-xs text-zinc-500">–</span>
            <input
              type="number"
              value={priceRange[1]}
              onChange={(e) => setPriceRange([priceRange[0], +e.target.value || facets.max_price])}
              data-testid="filter-price-max-input"
              className="w-24 border-2 border-black bg-white px-2 py-2 text-xs text-black focus:outline-none"
              placeholder="Max"
            />
          </div>
          <input
            type="range"
            min={facets.min_price}
            max={facets.max_price}
            value={priceRange[1]}
            onChange={(e) => setPriceRange([priceRange[0], +e.target.value])}
            data-testid="filter-price-slider"
            className="min-w-0 flex-1 accent-black"
          />
        </div>

        {/* Variants — multi-select */}
        {facets.variants?.length > 0 && (
          <div className="flex items-center gap-2 border-t border-black/10 pt-3 sm:border-none sm:pt-0" data-testid="filter-variant-chips">
            <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-500">
              {facets.variant_label || "Option"}:
            </span>
            <div className="flex flex-wrap gap-1.5">
              {facets.variants.map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => toggleVariant(v)}
                  data-testid={`filter-variant-${v.replace(/[^a-z0-9]/gi, "")}`}
                  aria-pressed={selectedVariants.includes(v)}
                  className={`border-2 px-3 py-1 text-[11px] font-bold uppercase tracking-widest transition-colors duration-200 ${
                    selectedVariants.includes(v)
                      ? "border-black bg-black text-white"
                      : "border-black/20 text-black hover:border-black"
                  }`}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>
        )}

        <button
          type="button"
          onClick={() => setInStockOnly(!inStockOnly)}
          data-testid="filter-instock-toggle"
          aria-pressed={inStockOnly}
          className={`whitespace-nowrap border-2 px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest transition-colors duration-200 ${
            inStockOnly ? "border-black bg-black text-white" : "border-black/20 text-black hover:border-black"
          }`}
        >
          In stock only
        </button>

        <SortSelect sort={sort} setSort={setSort} />

        {activeCount > 0 && (
          <button
            type="button"
            onClick={onReset}
            data-testid="filter-reset-btn"
            className="whitespace-nowrap border-2 border-black bg-white px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-black hover:bg-black hover:text-white"
          >
            Reset ({activeCount})
          </button>
        )}
      </div>
    </div>
  );
}

// Sidebar variant kept only for the mobile drawer
function FiltersPanel({ facets, priceRange, setPriceRange, selectedVariants, toggleVariant, inStockOnly, setInStockOnly, sort, setSort, activeCount, onReset, showResetAlways = false, className = "" }) {
  if (!facets) return null;
  return (
    <div className={className}>
      <div className="flex items-center justify-between border-b-2 border-black pb-3">
        <div className="flex items-center gap-2">
          <SlidersHorizontal size={16} />
          <h3 className="font-display text-sm font-black uppercase tracking-tight text-black">Filters</h3>
        </div>
        {(activeCount > 0 || showResetAlways) && (
          <button
            type="button"
            onClick={onReset}
            data-testid="filter-reset-btn"
            className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 hover:text-black"
          >
            Reset ({activeCount})
          </button>
        )}
      </div>

      <div className="mt-6">
        <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-500">Price (USD)</p>
        <div className="mt-4 flex items-center gap-3">
          <input
            type="number"
            value={priceRange[0]}
            onChange={(e) => setPriceRange([Math.max(facets.min_price, +e.target.value || 0), priceRange[1]])}
            data-testid="filter-price-min-input"
            className="w-full border-2 border-black bg-white px-2 py-2 text-xs text-black focus:outline-none"
            placeholder="Min"
          />
          <span className="text-xs text-zinc-500">to</span>
          <input
            type="number"
            value={priceRange[1]}
            onChange={(e) => setPriceRange([priceRange[0], +e.target.value || facets.max_price])}
            data-testid="filter-price-max-input"
            className="w-full border-2 border-black bg-white px-2 py-2 text-xs text-black focus:outline-none"
            placeholder="Max"
          />
        </div>
        <input
          type="range"
          min={facets.min_price}
          max={facets.max_price}
          value={priceRange[1]}
          onChange={(e) => setPriceRange([priceRange[0], +e.target.value])}
          data-testid="filter-price-slider"
          className="mt-4 w-full accent-black"
        />
        <p className="mt-1 text-[10px] text-zinc-500">
          ${Math.floor(facets.min_price)} – ${Math.ceil(facets.max_price)}
        </p>
      </div>

      {facets.variants?.length > 0 && (
        <div className="mt-8 border-t border-black/10 pt-6">
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-500">
            {facets.variant_label || "Options"}
          </p>
          <div className="mt-3 flex flex-wrap gap-2" data-testid="filter-variant-chips">
            {facets.variants.map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => toggleVariant(v)}
                data-testid={`filter-variant-${v.replace(/[^a-z0-9]/gi, "")}`}
                aria-pressed={selectedVariants.includes(v)}
                className={`border-2 px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest transition-colors duration-200 ${
                  selectedVariants.includes(v)
                    ? "border-black bg-black text-white"
                    : "border-black/20 text-black hover:border-black"
                }`}
              >
                {v} <span className="ml-1 opacity-50">({facets.variant_counts[v] || 0})</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="mt-8 border-t border-black/10 pt-6">
        <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-500">Availability</p>
        <button
          type="button"
          onClick={() => setInStockOnly(!inStockOnly)}
          data-testid="filter-instock-toggle"
          aria-pressed={inStockOnly}
          className={`mt-3 border-2 px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest transition-colors duration-200 ${
            inStockOnly ? "border-black bg-black text-white" : "border-black/20 text-black hover:border-black"
          }`}
        >
          In stock only
        </button>
      </div>

      <div className="mt-8 border-t border-black/10 pt-6">
        <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-500">Sort by</p>
        <div className="mt-3">
          <SortSelect sort={sort} setSort={setSort} full testId="filter-sort-select-mobile" />
        </div>
      </div>
    </div>
  );
}

const SORT_OPTIONS = [
  ["newest", "Newest"],
  ["featured", "Featured"],
  ["price_asc", "Price: low to high"],
  ["price_desc", "Price: high to low"],
  ["discount", "Biggest discount"],
];

function SortSelect({ sort, setSort, full = false, testId = "filter-sort-select" }) {
  return (
    <label className={`relative ${full ? "block" : "shrink-0"}`}>
      <span className="sr-only">Sort products</span>
      <select
        value={sort}
        onChange={(e) => setSort(e.target.value)}
        data-testid={testId}
        className={`cursor-pointer appearance-none border-2 border-black bg-white py-2 pl-3 pr-8 text-[11px] font-bold uppercase tracking-widest text-black focus:outline-none ${full ? "w-full" : ""}`}
      >
        {SORT_OPTIONS.map(([v, label]) => (
          <option key={v} value={v}>{label}</option>
        ))}
      </select>
      <ChevronDown size={13} className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-black" />
    </label>
  );
}

// Removable chips for whatever is currently filtering the grid.
function ActiveFilterChips({ facets, priceReady, priceRange, setPriceRange, selectedVariants, toggleVariant, inStockOnly, setInStockOnly, sort, setSort, onReset }) {
  const chips = [];
  const lo = facets ? Math.floor(facets.min_price) : 0;
  const hi = facets ? Math.ceil(facets.max_price) : 0;
  if (priceReady && (priceRange[0] > lo || priceRange[1] < hi)) {
    chips.push({
      key: "price",
      label: `$${priceRange[0]} – $${priceRange[1]}`,
      clear: () => setPriceRange([lo, hi]),
    });
  }
  selectedVariants.forEach((v) => chips.push({ key: `v-${v}`, label: v, clear: () => toggleVariant(v) }));
  if (inStockOnly) chips.push({ key: "stock", label: "In stock", clear: () => setInStockOnly(false) });
  if (sort !== "newest") {
    const label = (SORT_OPTIONS.find(([v]) => v === sort) || [])[1];
    chips.push({ key: "sort", label: `Sorted: ${label}`, clear: () => setSort("newest") });
  }
  if (!chips.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="active-filter-chips">
      {chips.map((c) => (
        <button
          key={c.key}
          type="button"
          onClick={c.clear}
          data-testid={`active-chip-${c.key}`}
          className="inline-flex items-center gap-1.5 border border-black bg-white px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-black transition-colors duration-200 hover:bg-black hover:text-white"
        >
          {c.label} <X size={11} />
        </button>
      ))}
      <button
        type="button"
        onClick={onReset}
        data-testid="active-chips-clear-all"
        className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 underline underline-offset-4 hover:text-black"
      >
        Clear all
      </button>
    </div>
  );
}

export default function CategoryPage() {
  const { categorySlug } = useParams();
  const navigate = useNavigate();
  const { key: locKey } = useLocation();
  const navType = useNavigationType();
  const [category, setCategory] = useState(null);
  const [products, setProducts] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [total, setTotal] = useState(0);
  const [productsLoading, setProductsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [catLoadFailed, setCatLoadFailed] = useState(false);
  const [retryTick, setRetryTick] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  // `?all=1` (used by the prerenderer) keeps auto-loading until every product
  // for this category is in the DOM, so the static HTML Googlebot crawls
  // contains every SKU link and an ItemList whose numberOfItems matches.
  const renderAllRef = useRef(new URLSearchParams(window.location.search).get("all") === "1");
  const renderAll = renderAllRef.current;
  const [facets, setFacets] = useState(null);
  const [priceRange, setPriceRange] = useState([0, 0]);
  // Filters live in the URL, so initialise from it (a shared/bookmarked link or
  // a refresh must land on the same filtered listing).
  const initialSp = useMemo(() => new URLSearchParams(typeof window === "undefined" ? "" : window.location.search), []);
  const [selectedVariants, setSelectedVariants] = useState(() => (initialSp.get("variants") || "").split(",").filter(Boolean));
  const [sort, setSort] = useState(() => initialSp.get("sort") || "newest");
  const [inStockOnly, setInStockOnly] = useState(() => initialSp.get("in_stock") === "1");
  // Params already applied, keyed by slug — so a StrictMode double-effect (or a
  // re-run after the sync effect rewrote the query string) can't wipe them.
  const appliedParamsRef = useRef({ slug: null, sp: initialSp });
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [faqs, setFaqs] = useState([]);
  const sentinelRef = useRef(null);
  // On back/forward navigation, reload as many products as were loaded
  // before (so scroll restoration can reach the exact previous position).
  const restoreCountRef = useRef(0);
  const initialLoadDoneRef = useRef(false);
  // Monotonic request version — every first-page fetch bumps it; any response
  // carrying an older version is stale (user changed category/filters while it
  // was in flight) and gets discarded instead of overwriting fresh data.
  const reqIdRef = useRef(0);

  // Load category + facets once per slug
  useEffect(() => {
    const sp =
      appliedParamsRef.current.slug === categorySlug
        ? appliedParamsRef.current.sp // same category — reuse what we already applied
        : new URLSearchParams(window.location.search); // navigated to a new category
    appliedParamsRef.current = { slug: categorySlug, sp };
    setSort(sp.get("sort") || "newest");
    setInStockOnly(sp.get("in_stock") === "1");
    setSelectedVariants((sp.get("variants") || "").split(",").filter(Boolean));
    restoreCountRef.current = navType === "POP" ? readLocationState(locKey)?.count || 0 : 0;
    initialLoadDoneRef.current = false;
    setNotFound(false);
    setCategory(null);
    setFacets(null);
    setPriceRange([0, 0]);
    setProducts([]);
    setTotal(0);
    setLoadError(false);
    setCatLoadFailed(false);
    getEntity(`/categories/${categorySlug}`).then((res) => {
      if (res.missing) return setNotFound(true);
      if (res.error) return setCatLoadFailed(true);
      // Backend returns `_new_slug` when the requested slug was renamed —
      // redirect the browser to the canonical URL so external links, SEO
      // crawlers and users bookmarking the old slug all land in the right place.
      if (res.data && res.data._new_slug && res.data._new_slug !== categorySlug) {
        navigate(`/${res.data._new_slug}`, { replace: true });
        return;
      }
      setCategory(res.data);
    });
    api.get("/products-facets", { params: { category: categorySlug } })
      .then((r) => {
        const f = r.data || {};
        setFacets(f);
        const lo = Math.floor(f.min_price || 0);
        const hi = Math.ceil(f.max_price || 0);
        const urlLo = Number(sp.get("min_price"));
        const urlHi = Number(sp.get("max_price"));
        setPriceRange([
          urlLo >= lo && urlLo <= hi ? urlLo : lo,
          urlHi >= lo && urlHi <= hi ? urlHi : hi,
        ]);
      })
      .catch(() => setFacets({ variants: [], min_price: 0, max_price: 0 }));
    // FAQs — fetched at the page level so we can pipe them into the SEO
    // component's JSON-LD (in <head>, captured by prerender) AND still render
    // the visible accordion below the grid.
    api
      .get("/faqs", { params: { category_slug: categorySlug } })
      .then((r) => setFaqs(Array.isArray(r.data) ? r.data : []))
      .catch(() => setFaqs([]));
  }, [categorySlug]);

  // Serialized filter key — stable string so the fetch effect only refires
  // when filter VALUES actually change (not on every facets/priceRange object
  // identity change). Crucially this lets the first page load fire immediately,
  // in parallel with the facets request, instead of waiting for it (waterfall).
  const priceReady = !!facets && priceRange[1] > 0;

  const filterKey = useMemo(() => {
    const p = { category: categorySlug };
    if (priceReady) {
      if (priceRange[0] > Math.floor(facets.min_price)) p.min_price = priceRange[0];
      if (priceRange[1] < Math.ceil(facets.max_price)) p.max_price = priceRange[1];
    }
    if (selectedVariants.length) p.variant = selectedVariants.join(",");
    if (inStockOnly) p.in_stock = true;
    if (sort !== "newest") p.sort = sort;
    return JSON.stringify(p);
  }, [categorySlug, priceRange, selectedVariants, inStockOnly, sort, facets, priceReady]);

  // Mirror the active filters into the query string (replace, so the back
  // button still returns to the previous page rather than the last filter).
  useEffect(() => {
    const sp = new URLSearchParams();
    if (sort !== "newest") sp.set("sort", sort);
    if (inStockOnly) sp.set("in_stock", "1");
    if (selectedVariants.length) sp.set("variants", selectedVariants.join(","));
    if (priceReady) {
      if (priceRange[0] > Math.floor(facets.min_price)) sp.set("min_price", String(priceRange[0]));
      if (priceRange[1] < Math.ceil(facets.max_price)) sp.set("max_price", String(priceRange[1]));
    }
    if (renderAll) sp.set("all", "1");
    const qs = sp.toString();
    if (qs !== window.location.search.replace(/^\?/, "")) {
      navigate({ search: qs ? `?${qs}` : "" }, { replace: true });
    }
  }, [sort, inStockOnly, selectedVariants, priceRange, facets, priceReady, navigate, renderAll]);

  useEffect(() => {
    const reqId = ++reqIdRef.current;
    // First load after a back/forward navigation fetches everything the
    // user had previously loaded via infinite scroll, in one request.
    const limit = initialLoadDoneRef.current ? PAGE_SIZE : Math.max(PAGE_SIZE, restoreCountRef.current);
    setProductsLoading(true);
    setLoadError(false);
    api
      .get("/products-page", { params: { ...JSON.parse(filterKey), skip: 0, limit } })
      .then((r) => {
        if (reqId !== reqIdRef.current) return; // stale response — discard
        initialLoadDoneRef.current = true;
        setProducts(r.data.items);
        setTotal(r.data.total);
        setProductsLoading(false);
      })
      .catch(() => {
        if (reqId !== reqIdRef.current) return;
        setLoadError(true);
        setProductsLoading(false);
      });
  }, [filterKey, retryTick]);

  // Remember how many products are loaded for this history entry so a
  // future back navigation can restore the exact same page height.
  useEffect(() => {
    if (products.length > 0) saveLocationState(locKey, { count: products.length });
  }, [products.length, locKey]);

  const loadMore = useCallback(async () => {
    if (loadingMore || productsLoading || loadError || products.length >= total) return;
    const reqId = reqIdRef.current; // page version at call time
    setLoadingMore(true);
    try {
      const { data } = await api.get("/products-page", {
        params: { ...JSON.parse(filterKey), limit: PAGE_SIZE, skip: products.length },
      });
      if (reqId !== reqIdRef.current) return; // filters/category changed mid-flight — discard
      setProducts((prev) => {
        const seen = new Set(prev.map((p) => p.slug));
        return [...prev, ...data.items.filter((it) => !seen.has(it.slug))];
      });
      setTotal(data.total);
    } catch {
      // transient failure — the observer re-fires on the next products/loadingMore
      // change, so the next scroll intersection retries automatically
    } finally {
      setLoadingMore(false);
    }
  }, [filterKey, loadingMore, productsLoading, loadError, products.length, total]);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;
    const io = new IntersectionObserver((entries) => entries[0].isIntersecting && loadMore(), { rootMargin: "600px" });
    io.observe(node);
    return () => io.disconnect();
  }, [loadMore]);

  // Prerender mode: pull every remaining page without waiting for a scroll.
  useEffect(() => {
    if (!renderAll || productsLoading || loadingMore || loadError) return;
    if (total && products.length < total) loadMore();
  }, [renderAll, productsLoading, loadingMore, loadError, products.length, total, loadMore]);

  // Live price/stock overlay — the paginated payload may be CDN-cached, so
  // patch never-cached pricing/availability onto whatever is rendered.
  const slugKey = products.map((p) => p.slug).join(",");
  useEffect(() => {
    if (!slugKey) return;
    api
      .get("/products-live", { params: { slugs: slugKey } })
      .then((r) => {
        const live = r.data?.items || {};
        setProducts((prev) => prev.map((p) => (live[p.slug] ? { ...p, ...live[p.slug] } : p)));
      })
      .catch(() => {});
  }, [slugKey]);

  const activeCount =
    (priceReady && priceRange[0] > Math.floor(facets.min_price) ? 1 : 0) +
    (priceReady && priceRange[1] < Math.ceil(facets.max_price) ? 1 : 0) +
    selectedVariants.length +
    (inStockOnly ? 1 : 0);

  const toggleVariant = (v) =>
    setSelectedVariants((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]));

  const reset = () => {
    if (facets) setPriceRange([Math.floor(facets.min_price), Math.ceil(facets.max_price)]);
    setSelectedVariants([]);
    setInStockOnly(false);
    setSort("newest");
  };

  if (notFound) return <NotFound />;
  if (catLoadFailed) return <LoadError />;
  if (!category) return (
    <div className="mx-auto max-w-7xl px-4 py-16 sm:px-8" data-testid="category-loading">
      <div className="mb-6 h-3 w-40 animate-pulse bg-zinc-200" />
      <div className="mb-3 h-10 w-80 animate-pulse bg-zinc-200" />
      <div className="h-4 w-2/3 animate-pulse bg-zinc-100" />
      <div className="mt-12 grid grid-cols-2 gap-6 md:grid-cols-3 lg:grid-cols-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="aspect-square animate-pulse bg-zinc-100" />
        ))}
      </div>
    </div>
  );

  const hasMore = products.length < total;
  const showFilters = facets && (facets.variants?.length > 0 || facets.max_price > facets.min_price);

  return (
    <div data-testid="category-page" className="mx-auto max-w-7xl px-4 py-16 sm:px-8">
      <SEO
        title={category.meta_title || `${category.name} | VVS Lab Jewelry`}
        description={category.meta_description || category.description}
        image={category.image || products[0]?.images?.[0]}
        canonical={category.canonical_url}
        jsonLd={[
          breadcrumbLd([{ name: "Home", path: "/" }, { name: category.name, path: `/${category.slug}` }]),
          collectionPageLd(category, products),
          itemListLd(products, category.slug),
          // FAQPage JSON-LD is only emitted when the DB actually has FAQs for
          // this category — Google penalises pages with an empty FAQPage schema.
          ...(faqs.length ? [{
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((f) => ({
              "@type": "Question",
              name: f.question,
              acceptedAnswer: { "@type": "Answer", text: f.answer },
            })),
          }] : []),
        ]}
      />
      <nav className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-500" aria-label="Breadcrumb">
        <Link to="/" className="hover:text-black hover:underline">Home</Link> <span className="mx-2">/</span>{" "}
        <span className="text-black">{category.name}</span>
      </nav>
      <h1 className="mt-6 font-display text-4xl font-black tracking-tighter text-black sm:text-5xl" data-testid="category-title">
        {category.name}
      </h1>
      {category.description && <p className="mt-4 text-base leading-relaxed text-zinc-600">{category.description}</p>}
      {category.description_top && (
        <div className="mt-8 border-t-2 border-black pt-6" data-testid="category-desc-top">{renderMd(category.description_top)}</div>
      )}

      {categorySlug === "moissanite-watches" && (
        <div className="mt-10">
          <ShopByTabs />
        </div>
      )}

      {/* Filters bar (desktop top) + grid */}
      {showFilters && (
        <div className="mt-10 hidden lg:block">
          <FiltersBar
            facets={facets}
            priceRange={priceRange}
            setPriceRange={setPriceRange}
            selectedVariants={selectedVariants}
            toggleVariant={toggleVariant}
            inStockOnly={inStockOnly}
            setInStockOnly={setInStockOnly}
            sort={sort}
            setSort={setSort}
            activeCount={activeCount}
            onReset={reset}
          />
        </div>
      )}

      {/* Mobile filter trigger */}
      {showFilters && (
        <button
          type="button"
          onClick={() => setMobileFiltersOpen(true)}
          data-testid="category-filters-mobile-btn"
          className="mt-10 inline-flex w-max items-center gap-2 border-2 border-black bg-white px-4 py-2 text-xs font-bold uppercase tracking-widest text-black lg:hidden"
        >
          <SlidersHorizontal size={14} /> Filters
          {activeCount > 0 && <span className="ml-1 bg-black px-2 py-0.5 text-[10px] text-white">{activeCount}</span>}
        </button>
      )}

      <div className="mt-6">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 text-xs text-zinc-500">
          <span data-testid="category-result-count">
            {productsLoading ? "Loading…" : `${total} ${total === 1 ? "piece" : "pieces"}`}
          </span>
          <ActiveFilterChips
            facets={facets}
            priceReady={priceReady}
            priceRange={priceRange}
            setPriceRange={setPriceRange}
            selectedVariants={selectedVariants}
            toggleVariant={toggleVariant}
            inStockOnly={inStockOnly}
            setInStockOnly={setInStockOnly}
            sort={sort}
            setSort={setSort}
            onReset={reset}
          />
        </div>

        <div
          className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5"
          data-testid="category-products-grid"
        >
          {products.map((p, i) => (
            <ProductCard key={p.slug} product={p} index={i} />
          ))}
        </div>

        {/* Skeletons while the first page is in flight — never flash "0 pieces" */}
        {productsLoading && products.length === 0 && (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5" data-testid="category-products-skeleton">
            {Array.from({ length: 10 }).map((_, i) => (
              <div key={i}>
                <div className="aspect-square animate-pulse bg-zinc-100" />
                <div className="mt-3 h-3 w-3/4 animate-pulse bg-zinc-100" />
                <div className="mt-2 h-3 w-1/3 animate-pulse bg-zinc-100" />
              </div>
            ))}
          </div>
        )}

        {loadError && (
          <div className="mt-16 border-2 border-black bg-white p-12 text-center" data-testid="category-load-error">
            <p className="text-zinc-600">Couldn&apos;t load products — check your connection and try again.</p>
            <button
              type="button"
              onClick={() => setRetryTick((t) => t + 1)}
              data-testid="category-retry-btn"
              className="mt-6 border-2 border-black bg-black px-8 py-3 text-xs font-bold uppercase tracking-widest text-white hover:bg-white hover:text-black"
            >
              Retry
            </button>
          </div>
        )}

        {products.length === 0 && !productsLoading && !loadError && (
          <div className="mt-16 border-2 border-black bg-white p-12 text-center" data-testid="category-empty">
            <p className="text-zinc-600">No pieces match those filters.</p>
            {activeCount > 0 && (
              <button
                type="button"
                onClick={reset}
                className="mt-6 border-2 border-black bg-black px-6 py-2 text-xs font-bold uppercase tracking-widest text-white hover:bg-white hover:text-black"
              >
                Reset filters
              </button>
            )}
          </div>
        )}

        {hasMore && !loadError && !productsLoading && (
          <div ref={sentinelRef} data-testid="category-infinite-sentinel" className="mt-8 flex justify-center py-8">
            <span className="inline-flex h-3 w-3 animate-pulse rounded-full bg-black" />
            <span className="ml-3 text-[11px] font-bold uppercase tracking-widest text-zinc-500">
              {loadingMore ? "Loading more…" : `Loading ${total - products.length} more`}
            </span>
          </div>
        )}
        {!hasMore && products.length > 0 && (
          <p className="mt-12 text-center text-[10px] uppercase tracking-[0.3em] text-zinc-400" data-testid="category-list-end">— End ({products.length} of {total}) —</p>
        )}
      </div>

      {category.description_bottom && (
        <div className="mt-16 border-t-2 border-black pt-8" data-testid="category-desc-bottom">
          {renderMd(category.description_bottom)}
        </div>
      )}

      <CategoryFAQs faqs={faqs} />

      {/* Mobile filter drawer */}
      {showFilters && (
        <div
          className={`fixed inset-0 z-[70] overflow-hidden transition-opacity duration-300 lg:hidden ${mobileFiltersOpen ? "pointer-events-auto visible opacity-100" : "pointer-events-none invisible opacity-0"}`}
          data-testid="category-filters-drawer"
        >
          <div className="absolute inset-0 bg-black/60" onClick={() => setMobileFiltersOpen(false)} />
          <aside
            className={`absolute right-0 top-0 flex h-full w-[85%] max-w-sm flex-col overflow-y-auto border-l-4 border-black bg-white p-6 shadow-2xl transition-transform duration-300 ${mobileFiltersOpen ? "translate-x-0" : "translate-x-full"}`}
          >
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-display text-base font-black uppercase tracking-tight text-black">Filters</h3>
              <button type="button" onClick={() => setMobileFiltersOpen(false)} data-testid="category-filters-close" className="flex h-9 w-9 items-center justify-center border-2 border-black bg-white text-black hover:bg-black hover:text-white">
                <X size={16} />
              </button>
            </div>
            <FiltersPanel
              facets={facets}
              priceRange={priceRange}
              setPriceRange={setPriceRange}
              selectedVariants={selectedVariants}
              toggleVariant={toggleVariant}
              inStockOnly={inStockOnly}
              setInStockOnly={setInStockOnly}
              sort={sort}
              setSort={setSort}
              activeCount={activeCount}
              onReset={reset}
              showResetAlways
            />
            <button
              type="button"
              onClick={() => setMobileFiltersOpen(false)}
              className="mt-6 w-full border-2 border-black bg-black py-3 text-xs font-bold uppercase tracking-[0.25em] text-white hover:bg-white hover:text-black"
            >
              Apply
            </button>
          </aside>
        </div>
      )}
    </div>
  );
}
